import React, { FC, useState, useMemo, useEffect } from "react";
import _ from "lodash";
import { AppliedFilters, FilterType } from "../types";

type Props = {
  initialFilters: AppliedFilters;
  onFiltersApply: (filters) => void;
};

type Actions = {
  onSelect(type: FilterType, value): void;
  onRemove(type: FilterType): void;
  onApply(): void;
  onRefresh(): void;
};

type FiltersContextType = {
  filters: AppliedFilters;
  hasFilters: boolean;
  hasInitialFilters: boolean;
  isApplied: (type: FilterType, value) => boolean;
  actions: Actions;
};

const initialValue: FiltersContextType = {
  filters: {},
  hasFilters: false,
  hasInitialFilters: true,
  isApplied: () => false,
  actions: {
    onSelect: (): void => {
      return;
    },
    onRemove: (): void => {
      return;
    },
    onApply: (): void => {
      return;
    },
    onRefresh: (): void => {
      return;
    },
  },
};

export const FiltersContext = React.createContext<FiltersContextType>(
  initialValue
);

const FiltersProvider: FC<Props> = ({
  initialFilters,
  onFiltersApply,
  children,
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [activeFilters, setActiveFilters] = useState(initialFilters);
  const hasFilters = useMemo(() => !_.isEqual(filters, activeFilters), [
    filters,
    activeFilters,
  ]);
  const hasInitialFilters = useMemo(
    () => _.isEqual(activeFilters, initialFilters),
    [activeFilters, initialFilters]
  );
  const onSelect = (type, value): void => {
    setFilters((prev) => {
      return {
        ...prev,
        [type]: {
          ...prev[type],
          currentValue: value,
        },
      };
    });
  };
  const onRemove = (type): void => {
    setFilters((prev) => {
      return {
        ...prev,
        [type]: {
          ...prev[type],
          currentValue: null,
        },
      };
    });
  };
  const onApply = (): void => {
    setActiveFilters(filters);
  };
  const onRefresh = (): void => {
    setFilters(initialFilters);
    setActiveFilters(initialFilters);
  };
  function isApplied(filterType: FilterType, value): boolean {
    if (!filters[filterType]) {
      return false;
    }
    const selectedValue =
      // @ts-ignore
      filters[filterType].currentValue || filters[filterType].defaultValue;
    return selectedValue === value;
  }
  useEffect(() => {
    const newFilters = Object.keys(activeFilters)
      .filter(
        (filterKey) =>
          activeFilters[filterKey].currentValue ||
          activeFilters[filterKey].defaultValue
      )
      .reduce((acum, filterKey) => {
        return {
          ...acum,
          [filterKey]:
            activeFilters[filterKey].currentValue ||
            activeFilters[filterKey].defaultValue,
        };
      }, {});
    onFiltersApply(newFilters);
  }, [activeFilters]);
  return (
    <FiltersContext.Provider
      value={{
        filters,
        hasFilters,
        hasInitialFilters,
        isApplied,
        actions: { onSelect, onRemove, onApply, onRefresh },
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

export default FiltersProvider;
